import { GraphQLContext } from '@thg-commerce/enterprise-core/src/graphql/types'
import { getVariantSavingValue } from '@thg-commerce/enterprise-pricing/src/getVariantSavingValue'
import { personalisationImagesTransformer } from '@thg-commerce/enterprise-product-options/src/utils/options'
import { sortPersonalisationFieldsByOrder } from '@thg-commerce/enterprise-product-options/src/utils/sortOptions'

import {
  FreeTextProductPersonalisationField,
  HOME_URL,
  MultiSelectionProductPersonalisationField,
  PersonalisationData,
  SingleSelectionProductPersonalisationField,
  SubscriptionChoice,
  SubscriptionData,
} from '../../ApolloProvider/resolvers/Types/Product'
import {
  AvailabilitySource,
  ProductImage,
  RichContentItem,
  SubscriptionContract,
  SubscriptionFrequency,
} from '../../generated/graphql'

import { productContent } from './content'
import { SubscriptionOptionsPaymentType } from './defaultVariant'
import { productDescription } from './description'
import { productGallery } from './gallery'
import { getPowerReviewSchema } from './getPowerReviewSchema'
import {
  MarketedSpecialOfferSummary,
  productMarketedSpecialOffer,
} from './marketedSpecialOffer'

export const checkNotifyWhenInStockOnProduct = (
  variants: { inStock: boolean; notifyWhenInStockEnabled?: boolean | null }[],
) => {
  return variants.some(
    (variant) => variant.inStock === false && variant.notifyWhenInStockEnabled,
  )
}

export interface ProductStockInformationConfigs {
  showMessage?: boolean
  showPrefixText?: boolean
  showSuffixText?: boolean
}

const STOCK_INFORMATION_SEPARATOR = ' | '

export const generateAvailabilityMessage = (
  variant: Product['variants'][0],
  configs?: ProductStockInformationConfigs,
) => {
  if (!variant?.availabilityMessage || !configs?.showMessage || !configs) {
    return ''
  }
  const availabilityMessageSplit = variant.availabilityMessage.split(
    STOCK_INFORMATION_SEPARATOR,
  )

  const messagePrefix: string = configs.showPrefixText
    ? availabilityMessageSplit[0] || ''
    : ''
  const messageSuffix: string = configs.showSuffixText
    ? availabilityMessageSplit[1] || ''
    : ''
  const messageSeparator: string =
    messagePrefix && messageSuffix ? STOCK_INFORMATION_SEPARATOR : ''

  return `${messagePrefix}${messageSeparator}${messageSuffix}`
}

interface Product {
  sku: number
  images: ProductImage[]
  title: string
  url: string
  breadcrumbs?:
    | {
        pagePath: string
        displayName: string
        __typename?: string
      }[]
    | null
  reviews?: {
    total: number
    averageScore: number
  } | null
  variants: {
    sku: number
    title: string
    externalIdentifier?: string
    price?: {
      price: {
        displayValue: string
        scalarValue: string
        amount: string
      }
      rrp: {
        displayValue: string
        scalarValue: string
        amount: string
      }
    } | null
    vipPrice?: {
      displayValue: string
      amount: string
    } | null
    product?: {
      sku: number
      url: string
      title: string
      images?: ProductImage[]
      breadcrumbs?:
        | {
            pagePath: string
            displayName: string
            __typename?: string
          }[]
        | null
      reviews?: {
        total: number
        averageScore: number
      } | null
      content: {
        key: string
        value: any
      }[]
    } | null
    images: {}[]
    returnsInformation: RichContentItem | null
    deliveryInformation: RichContentItem | null
    isSubscription: boolean
    isAutoRenewSubscription: boolean
    subscriptionFrequency?: SubscriptionFrequency
    subscriptionTerm?: number
    subscriptionPaymentType?: SubscriptionOptionsPaymentType
    subscriptionContracts: SubscriptionContract[]
    choices: SubscriptionChoice[]
    availabilityMessage?: string
    availabilitySource: AvailabilitySource
    personalisationFields?: [
      | MultiSelectionProductPersonalisationField
      | FreeTextProductPersonalisationField
      | SingleSelectionProductPersonalisationField,
    ]
    personalisationData?: PersonalisationData
  }[]
}

type ProductVariant<V extends Product['variants'][0]> = V & {
  gallery: ReturnType<typeof productGallery>
  url: string | null
  subtitle: string | null
  reviews: { total: number; averageScore: number } | null
  breadcrumbs:
    | {
        url: string
        text: string
      }[]
    | null
  breadcrumbSchemaItems:
    | {
        '@type': 'ListItem' | string
        position: number
        name: string
        item: string
      }[]
    | null
  product:
    | (V['product'] & {
        description: ReturnType<typeof productDescription> | null
      })
    | null
  returnsInformation: RichContentItem | null
  deliveryInformation: RichContentItem | null
  subscriptionData: SubscriptionData
  marketedSpecialOffer: MarketedSpecialOfferSummary | null
  masterSkuTitle?: string
  personalisationFields?: [
    | MultiSelectionProductPersonalisationField
    | FreeTextProductPersonalisationField
    | SingleSelectionProductPersonalisationField,
  ]
  personalisationData?: PersonalisationData
}

export const productVariants = async <P extends Product>(
  product: P,
  config: {
    originUrl: string
  },
  descriptionKeys?: {
    items: string[]
    details: string[]
    video: string[]
    download: string[]
  },
  cheapestVariantSKU?: number,
  productStockInformation?: ProductStockInformationConfigs,
  tags?: string[],
  productPersonalisationThemeOrder?: string[],
  powerReviewConfig?: GraphQLContext['config'],
): Promise<{
  variants: ProductVariant<P['variants'][0]>[]
  cheapestVariant: ProductVariant<P['variants'][0]> | null
  subscriptionData: SubscriptionData | null
}> => {
  let cheapestVariantTransformed: ProductVariant<P['variants'][0]> | null = null
  let selectedVariantSubscriptionData: SubscriptionData | null = null

  const subscriptionOptions = {
    upfront: (product?.variants || []).reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.UPFRONT
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
    onDispatch: (product?.variants || []).reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.ON_DISPATCH
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
  }

  const indexedVariants = product.variants.reduce<{
    [sku: number]: ProductVariant<P['variants'][0]>[]
  }>((accumulator, variant) => {
    const masterProductImages =
      variant.product?.images && variant.product.images.length > 0
    const variantImages = variant.images && variant.images.length > 0
    let linkedSku = false
    if (variant.product?.sku && variant.product.sku !== product.sku) {
      linkedSku = true
    }
    if (!accumulator[variant.product?.sku || variant.sku]) {
      accumulator[variant.product?.sku || variant.sku] = []
    }

    const images =
      masterProductImages && linkedSku
        ? variant.product?.images
        : variantImages
        ? variant.images
        : product.images || []

    const gallery = variantImages
      ? productGallery(variant, tags)
      : masterProductImages
      ? productGallery(variant?.product, tags) // type error
      : null

    const content = variant.product
      ? productContent(variant.product, {
          subtitle: ['subtitle'],
        })
      : null

    const breadcrumbs = [
      ...(variant?.product?.breadcrumbs?.map((breadcrumb) => ({
        text: breadcrumb.displayName,
        url:
          breadcrumb.pagePath === HOME_URL
            ? breadcrumb.pagePath
            : breadcrumb.pagePath.concat('.list'),
        __typename: breadcrumb.__typename,
      })) || []),
    ]

    if (variant?.product) {
      breadcrumbs.push({
        text: variant.title,
        url: variant.product.url,
        __typename: 'Breadcrumb',
      })
    }

    const breadcrumbSchemaItems = breadcrumbs.map((breadcrumb, index) => {
      return {
        '@type': 'ListItem',
        position: index + 1,
        name: breadcrumb.text,
        item: config.originUrl.concat(breadcrumb.url),
        __typename: 'BreadcrumbSchemaItems',
      }
    })

    const transformedPrice = variant?.vipPrice
      ? {
          ...variant.price,
          price: variant?.vipPrice,
        }
      : variant.price

    const personalisationFields = variant.personalisationFields?.map(
      (personalisationField) => {
        if (
          personalisationField.__typename !==
          'FreeTextProductPersonalisationField'
        ) {
          return {
            ...personalisationField,
            choices: personalisationField?.options.map((option) => {
              return {
                key: option.previewAssetSetIdentifier || option.name,
                colour: '',
                title: option.value,
                disabled: false,
                image: option.displayAsset?.url || '',
              }
            }),
          }
        }
        return personalisationField
      },
    )

    const transformedVariant: ProductVariant<P['variants'][0]> = {
      ...variant,
      gallery,
      images,
      breadcrumbs,
      breadcrumbSchemaItems,
      personalisationFields: sortPersonalisationFieldsByOrder(
        personalisationFields,
        productPersonalisationThemeOrder,
      ),
      personalisationData: {
        ...variant.personalisationData,
        personalisationSupportImages: personalisationImagesTransformer(variant),
      },
      price: transformedPrice,
      url: product.url ?? null,
      reviews: variant.product?.reviews ?? null,
      masterSkuTitle: variant?.product?.title || product.title,
      subtitle:
        content?.subtitle &&
        content.subtitle.length > 0 &&
        'richContentValue' in content.subtitle[0].value &&
        content.subtitle[0].value.richContentValue.content[0]
          ? content.subtitle[0].value.richContentValue.content[0].content
          : null,
      product: variant.product
        ? {
            ...variant.product,
            description: descriptionKeys
              ? productDescription(variant.product, descriptionKeys)
              : null,
          }
        : null,
      returnsInformation: variant.returnsInformation?.content?.[0]?.content
        ? variant.returnsInformation.content[0].content
        : null,
      deliveryInformation: variant.deliveryInformation?.content?.[0]?.content
        ? variant.deliveryInformation.content[0].content
        : null,
      subscriptionData: {
        isSubscription: variant.isSubscription ?? false,
        isAutoRenewSubscription: variant.isAutoRenewSubscription ?? false,
        subscriptionFrequency: variant.subscriptionFrequency ?? null,
        subscriptionTerm: variant.subscriptionTerm ?? null,
        subscriptionPaymentType: variant.subscriptionPaymentType ?? null,
        subscriptionContracts: variant.subscriptionContracts ?? [],
        choices:
          variant.isSubscription && variant.choices.length
            ? [
                {
                  ...variant.choices[0],
                  price: variant.price?.price.displayValue,
                  saving: variant.price
                    ? getVariantSavingValue(variant.price)
                    : null,
                },
              ]
            : [],
        subscribeOptions: subscriptionOptions,
      },
      marketedSpecialOffer: variant.marketedSpecialOffer
        ? productMarketedSpecialOffer(variant)
        : null,
      availabilityMessage: generateAvailabilityMessage(
        variant,
        productStockInformation,
      ),
      availabilitySource: variant.availabilitySource
        ? variant.availabilitySource
        : AvailabilitySource.Warehouse,
    }

    if (cheapestVariantSKU === variant.sku) {
      cheapestVariantTransformed = transformedVariant
    }

    if (product.sku === variant.sku) {
      selectedVariantSubscriptionData = transformedVariant.subscriptionData
    }

    accumulator[variant.product?.sku || variant.sku].push(transformedVariant)
    return accumulator
  }, {})

  const transformedVariants: ProductVariant<P['variants'][0]>[] = Object.values(
    indexedVariants,
  ).flat()

  const variants = await Promise.all(
    transformedVariants.map(async (variant) => {
      const powerReviews = await getPowerReviewSchema({
        config: powerReviewConfig!,
        externalIdentifier: variant.externalIdentifier || undefined,
        sku: variant.sku,
      })

      if (powerReviews) {
        return { ...variant, powerReviews }
      }

      return variant
    }),
  )

  return {
    variants,
    cheapestVariant: cheapestVariantTransformed,
    subscriptionData: selectedVariantSubscriptionData,
  }
}
